import { Box, ColorOptions, Icon, Loader, Spacer, Text, TextInput } from '@jfs/ui-new'
import * as React from 'react'
import { FC, useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { initRefundSearchFiltersData, searchFiltersData } from '../../data/static/data'
import { actionMenuStrings } from '../../data/static/strings'
import { RootStore, useLazyApiData, useStores } from '../../data/store'
import { Routes } from '../../nav/routes'
import { shouldShowDateForSearch } from '../../util/common'
import {
  dateFormatter,
  DateFormatTypes,
  getHHMMSS,
  getSearchFilterKey,
  getSelectedDate,
  useWindowDimensions,
} from '../../util/util'
import { DropdownOptimised } from '../DropdownNew'
import { DropdownTypes } from '../DropdownNew/types'
import { ModBox } from '../ModifiedBox/ModifiedBox'
import { TableComponent } from '../TableComponent'
import { SearchListItem } from './SearchListItem'
import { styles } from './styles'

const searchDurationLabel = '* Upto 180 days old'
const searchLabel = 'Search for transactions..'
const labelRecents = 'RECENT SEARCHES'
const defaultValidatorForMinimumEntry = 3
export enum SearchBarTypes {
  DEFAULT = 'Default',
}
export interface SearchBarProps {
  type: SearchBarTypes
  path?: string
  navigation: any
  searchData: any[]
  onChange?: (searchString: string) => void
  onResultClicked: (show: boolean, txnId: string, filterKey: string) => void
}
export interface PortalProps {
  isListVisible: boolean
  noDataVisible: boolean
  data: Array<any>
  isRecents: boolean
  dimens: any
  setValue: (value: string) => void
  setListVisible: (value: boolean) => void
  setNoDataVisible: (value: boolean) => void
  handleItemPress: (item: any) => void
  screenWidth: number
  type: any
}

const RenderPortal = ({
  isListVisible,
  noDataVisible,
  data,
  isRecents,
  dimens,
  setValue,
  setListVisible,
  setNoDataVisible,
  handleItemPress,
  screenWidth,
}: PortalProps): React.ReactElement => {
  if (!isRecents || data?.length === 0) {
    return <></>
  }
  const handleClose = (): void => {
    setValue('')
    setListVisible(false)
    setNoDataVisible(false)
  }

  const keyExtractor = (itemData: any): string => {
    return itemData.id
  }

  const getSeperator = () => <Box height={1} borderColor={'GREY_SEPARATOR'} borderWidth={1} />

  const renderItem = (itemData: any) => (
    <SearchListItem data={itemData} onPress={handleItemPress} isRecents={isRecents} />
  )

  const getView = (): React.ReactElement => {
    if (isListVisible || noDataVisible) {
      return (
        <Box
          underlayColor={'TRANSPARENT'}
          testID="close-search-result"
          onPress={handleClose}
          {...styles(screenWidth).dropdown}
          borderWidth={1}>
          <Box
            {...styles(screenWidth).list}
            w={dimens[0]}
            borderTopColor={'BLUE'}
            borderTopWidth={1}
            top={dimens[1] + 30}
            left={dimens[2]}>
            <Box pv={12} ph={16}>
              <Text hide={!isRecents} {...styles(screenWidth).recentsTextStyle}>
                {labelRecents}
              </Text>
            </Box>
            {getSeperator()}
            <ModBox show={isListVisible} pl={16}>
              <TableComponent
                value={''} //Routes value
                canFetchMore={false}
                hide={false}
                isCustom={true}
                testID={'searchResultList'}
                ListContent={data ?? []}
                RenderCustomElement={renderItem}
                keyExtractor={keyExtractor}
                ItemSeparatorComponent={getSeperator}
              />
            </ModBox>
          </Box>
        </Box>
      )
    }
    return <></>
  }
  return ReactDOM.createPortal(getView(), document.body)
}

const SearchBar: FC<SearchBarProps> = ({ onResultClicked, navigation, type }: SearchBarProps) => {
  const {
    searchData,
    getSearchResults,
    mid,
    setSearchResultVisibility,
    setSearchString,
    searchString,
    showSearchResult,
    searchFilterItem,
    setSearchFilterItem,
    updateFieldSettings,
    updateRecents,
    getRecentSearches,
    updateSelectedDateInfo,
  } = useStores((store) => ({
    searchData: store.transactions.getAllTransactions(),
    getSearchResults: store.transactions.getSearchResultsTxnV3,
    mid: store.user.selectedMid,
    setSearchResultVisibility: store.settings.metaData.setSearchResultVisibility,
    setSearchString: store.settings.metaData.setSearchString,
    searchString: store.settings.metaData.searchString,
    showSearchResult: store.settings.metaData.showSearchResult,
    searchFilterItem: store.settings.metaData.searchFilterItem,
    setSearchFilterItem: store.settings.metaData.setSearchFilterItem,
    updateFieldSettings: store.transactions.updateFieldSetting,
    fieldSettings: store.transactions.fieldSetting,
    updateRecents: store.settings.metaData.updateSearhRecents,
    getRecentSearches: store.settings.metaData.getRecentSearches,
    updateSelectedDateInfo: store.transactions.updateSelectedDateInfo,
  }))

  const [searchApi, { state }] = useLazyApiData(getSearchResults)
  const screenWidth = useWindowDimensions()

  const [value, setValue] = useState('')
  const [isListVisible, setListVisible] = useState(false)
  const [noDataVisible, setNoDataVisible] = useState(false)
  const [data, setData] = useState([] as Array<any>)
  const [isRecents, setShowRecents] = useState(false)
  const [dimens, setDimens] = useState([0, 0, 0])

  const setTxnDetailsVisibility = useCallback(
    (itemValue, txnId, filterItem, txnDate) => onResultClicked(itemValue, txnId, filterItem, txnDate),
    [onResultClicked],
  )
  const getTodaysDateReq = () => {
    const formattedFilteredFromData = `${dateFormatter(getSelectedDate(1), DateFormatTypes.YYYYMMDD)}${getHHMMSS(
      getSelectedDate(1),
    )}`
    const formattedFilteredToDateDate = `${dateFormatter(new Date(), DateFormatTypes.YYYYMMDD)}${getHHMMSS(new Date())}`
    return { startDateTime: formattedFilteredFromData, endDateTime: formattedFilteredToDateDate }
  }

  const { navigate } = navigation
  useEffect(() => {
    setValue(searchString)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    if (state === 'fulfilled') {
      const searchResult = searchData?.length > 1 ? searchData?.slice(1) : []
      if (type === SearchBarTypes.DEFAULT) {
        if (searchResult?.length === 1) {
          setTxnDetailsVisibility(
            true,
            searchResult[0].transactionId,
            searchFilterItem,
            searchResult[0]?.transactionDateTime,
          )
          setSearchString(value)
          setSearchResultVisibility(true)
        } else {
          setSearchResultVisibility(true)
          setSearchString(value)
          navigate(Routes.TRANSACTIONS, { isTransactionApiCalled: true })
        }
      } else {
        setSearchResultVisibility(true)
        setSearchString(value)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const onSearchStringChanged = (text: string): void => {
    setValue(text)
    setShowRecents(false)
  }

  const handleSearchClick = useCallback(() => {
    if (value?.length > 0) {
      setSearchResultVisibility(false)
      const searchKey = RootStore.settings.metaData.searchFilterItem
      updateFieldSettings(getSearchFilterKey[searchKey])
      const showDateForSearch = shouldShowDateForSearch(searchKey)
      if (showDateForSearch) {
        updateSelectedDateInfo({
          fromDate: getSelectedDate(1),
          toDate: new Date(Date.now()),
          position: {
            top: 0,
            left: 0,
            visible: false,
          },
          dateLabel: actionMenuStrings?.today,
        })
      }

      searchApi(mid, value, type, undefined, showDateForSearch ? getTodaysDateReq() : undefined)
      updateRecents(value, searchKey)
    }
  }, [
    mid,
    searchApi,
    setSearchResultVisibility,
    type,
    updateFieldSettings,
    updateRecents,
    updateSelectedDateInfo,
    value,
  ])

  const handleItemPress = (itemData: any): void => {
    setValue(itemData?.item?.txnId)
    setListVisible(false)
    handleKeyChange(itemData?.item?.filterKey)
    const showDateForSearch = shouldShowDateForSearch(searchFilterItem)
    if (showDateForSearch) {
      updateSelectedDateInfo({
        fromDate: getSelectedDate(1),
        toDate: new Date(Date.now()),
        position: {
          top: 0,
          left: 0,
          visible: false,
        },
        dateLabel: actionMenuStrings?.today,
      })
    }
    searchApi(mid, itemData?.item?.txnId, type, undefined, showDateForSearch ? getTodaysDateReq() : undefined)
  }
  const handleClose = (): void => {
    setValue('')
    setListVisible(false)
    setNoDataVisible(false)
    setSearchResultVisibility(false)
    setSearchString('')
    navigate(Routes.TRANSACTIONS, { isTransactionApiCalled: false })
  }

  const getRecentsListData = (recents: any): void => {
    // const recentsParsedData: any[] = []
    // recents.map((recentItem: any) => {
    //   const newData = searchData?.filter((txnItem: any) => recentItem.txnId === txnItem?.id)[0]
    //   newData &&
    //     recentsParsedData.push({ key: recentItem.filterKey, value: newData[getSearchFilterKey[recentItem.filterKey]] })
    //   return newData
    // })
    setData(recents.slice(0, 5))
    setListVisible(recents.length > 0)
    setNoDataVisible(recents.length === 0)
  }
  const handleFocus = (): void => {
    const recentData = getRecentSearches(type)
    setShowRecents(recentData.length > 0 && value?.length === 0)
    recentData.length > 0 && getRecentsListData(recentData)
  }

  const handleOnChange = (text: string): void => {
    onSearchStringChanged(text)
  }

  const handleKeyChange = useCallback(
    (item: any) => {
      setSearchFilterItem(item)
      /**
       * Value must not be empty and it must be more than atleast minimum required to save API calls hit
       * if the input param is way to less.
       */
      value?.length > defaultValidatorForMinimumEntry && handleSearchClick()
    },
    [handleSearchClick, setSearchFilterItem, value],
  )

  const searchResult = searchData?.length > 1 ? searchData?.slice(1) : []
  const searchBarWidth = screenWidth * 0.7 - 42
  return (
    <>
      <RenderPortal
        isListVisible={isListVisible}
        noDataVisible={noDataVisible}
        data={data}
        isRecents={isRecents}
        dimens={dimens}
        setValue={setValue}
        setListVisible={setShowRecents}
        setNoDataVisible={setNoDataVisible}
        handleItemPress={handleItemPress}
        screenWidth={screenWidth}
        type={type}
      />
      <Box
        minWidth={type === SearchBarTypes.DEFAULT ? 480 : searchBarWidth}
        direction={'row'}
        align={'center'}
        pl={8}
        // justify="space-between"
        onLayout={(event) =>
          setDimens([event.nativeEvent.layout.width, event.nativeEvent.layout.top, event.nativeEvent.layout.left])
        }>
        <Box justify="space-between" direction={'row'} align={'center'} flexGrow={1}>
          <Box direction="row" align={'center'} testID="search-bar">
            <DropdownOptimised
              testID="search-dropdown"
              placeHolder={searchFilterItem ?? searchFiltersData[0].value}
              type={DropdownTypes.DEFAULT_WITH_BG}
              data={type === SearchBarTypes.DEFAULT ? searchFiltersData : initRefundSearchFiltersData}
              onChange={handleKeyChange}
            />
            <Spacer direction="horizontal" size={12} />
            <Box width={'90%'} flexGrow={1}>
              <TextInput
                value={value}
                type="body1"
                fontWeight="500"
                onFocusBottomLineColor="TRANSPARENT"
                bottomLineColor="TRANSPARENT"
                placeholder={searchLabel}
                placeholderTextColor="LIGHT_BLACK"
                onFocus={handleFocus}
                onChangeText={handleOnChange}
                editable={state !== 'pending'}
                testID="global-search"
              />
            </Box>
          </Box>
          <ModBox pointerEvents="none" {...styles(screenWidth).searchDurationViewStyle}>
            <Text hide={isListVisible || value?.trim()?.length > 0} type={'caption'} color={'PRIMARY_GREY'}>
              {searchDurationLabel}
            </Text>
            <ModBox show={state === 'pending'} flexDirection={'row'}>
              <Loader color={'#A0A0A0' as ColorOptions} type={12} />
              <Spacer direction={'horizontal'} size={8} />
              <Text type={'caption'} color={'PRIMARY_GREY'}>
                {'searching'}
              </Text>
            </ModBox>
            <ModBox show={showSearchResult && searchResult?.length === 0 && state !== 'pending'}>
              <Text type={'caption'} color={'PRIMARY_GREY'}>
                {'Oops!..No results found'}
              </Text>
            </ModBox>
          </ModBox>
        </Box>
        <Spacer direction="horizontal" size={12} />
        <Box
          {...styles(screenWidth).searchIconContainer}
          bg={type === SearchBarTypes.DEFAULT ? ('#0186B014' as ColorOptions) : 'TRANSPARENT'}
          onPress={
            state === 'pending' || (showSearchResult && searchString === value) ? handleClose : handleSearchClick
          }
          testID="search-btn">
          <Icon
            id={'SEARCH'}
            color={'BLUE'}
            size={16}
            hide={state === 'pending' || (showSearchResult && searchString === value)}
          />
          <Icon
            hide={!(state === 'pending' || (showSearchResult && searchString === value))}
            id={'CLOSE'}
            color={'BLUE'}
            size={16}
          />
        </Box>
      </Box>
    </>
  )
}

export default SearchBar
